import { useInfiniteQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@shared/api';
import { helpdeskTaskSchema } from './commands';
import type { HelpdeskListResponse, HelpdeskQuery } from './queries';

export type HelpdeskFilterOptions = NonNullable<HelpdeskListResponse['filterOptions']>;
type FilterResponse = HelpdeskListResponse & { filterOptions: HelpdeskFilterOptions };
const pagination = z.object({
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  totalItems: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
});
export const helpdeskFilterResponseSchema = z.object({
  items: z.array(helpdeskTaskSchema),
  meta: pagination,
  filterOptions: z.object({
    sources: z.array(z.object({ id: z.uuid(), dbValue: z.string(), display: z.string() })),
    users: z.object({
      items: z.array(z.object({ id: z.uuid(), displayName: z.string().nullable() })),
      meta: pagination,
    }),
  }),
}) satisfies z.ZodType<FilterResponse>;

const querySchema = z
  .object({
    includeFilterOptions: z.literal('1'),
    filterUserQ: z.string().trim().min(1).max(200).optional(),
    filterUserPage: z.number().int().min(1).max(100000),
    page: z.literal(1),
    pageSize: z.literal(10),
  })
  .strict();

export function helpdeskFilterOptionsKey(scope: string, search: string) {
  return ['helpdesk', scope, 'filter-options', search.trim()] as const;
}

/** Mounted only while Filters is open; reopening always checks current source authority. */
export function useHelpdeskFilterOptions(scope: string, search: string) {
  const q = search.trim();
  return useInfiniteQuery({
    queryKey: helpdeskFilterOptionsKey(scope, q),
    queryFn: async ({ pageParam, signal }) => {
      const params = querySchema.parse({
        includeFilterOptions: '1',
        filterUserQ: q || undefined,
        filterUserPage: pageParam,
        page: 1,
        pageSize: 10,
      }) satisfies HelpdeskQuery;
      return await api.get('/api/v1/tasks', {
        params,
        signal,
        schema: helpdeskFilterResponseSchema,
      });
    },
    initialPageParam: 1,
    getNextPageParam: ({ filterOptions }) => {
      const { page, totalPages } = filterOptions.users.meta;
      return page < totalPages ? page + 1 : undefined;
    },
    enabled: scope.length > 0,
    retry: false,
    gcTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}
